/** Outgoing HTTP: core config, telemetry, Bearer token. */
import { AppConfig } from '@core/domain/AppConfig.ts';
import type { TelemetryPort } from '@core/domain/ports/driven/TelemetryPort.ts';
import { AUTH_SESSION_EXPIRED_EVENT } from '@core/domain/sessionEvents.ts';
import type { RetryOptions } from '@core/domain/utils/asyncResilience.ts';
import {
  fetchWithTimeoutAndRetry,
  type HttpFetchRequestOptions,
  type HttpTransportResponse,
} from './httpFetchWithRetry.ts';

/** HTTP bearer token read/clear; satisfied structurally by auth storage adapters. */
type HttpTokenStorage = {
  getToken(): string | null;
  clearToken(): void;
};

interface HttpClientOptions extends HttpFetchRequestOptions {
  /** Treat 401 as a plain error without clearing session (e.g. login). */
  treat401AsPlainError?: boolean;
}

/** Converts a HeadersInit object into a plain Record<string, string> for manipulation. */
function asHeaderRecord(extra: HeadersInit): Record<string, string> {
  return typeof extra === 'object' && extra !== null && !Array.isArray(extra) ? { ...(extra as Record<string, string>) } : {};
}

export class HttpClient {
  private readonly defaultTimeout = 10000;
  private readonly maxRetries = 2;

  private readonly retryBackoff: RetryOptions = {
    maxAttempts: 8,
    baseDelayMs: 350,
    maxDelayMs: 8000,
    jitterRatio: 0.25,
  };

  /** Initializes the HttpClient with token storage and telemetry ports. */
  constructor(
    private readonly tokenStorage: HttpTokenStorage,
    private readonly telemetry: TelemetryPort,
  ) {}

  /** Builds HTTP headers including authorization, correlation ID, and CSRF tokens. */
  private getAuthHeaders(extra: HeadersInit = {}, correlationId?: string): HeadersInit {
    const headers = asHeaderRecord(extra);
    headers['X-Requested-With'] = 'XMLHttpRequest';
    if (correlationId) headers['X-Correlation-ID'] = correlationId;
    const token = this.tokenStorage.getToken();
    if (token) headers.Authorization = `Bearer ${token}`;
    const csrf = document.querySelector('meta[name="csrf-token"]');
    if (csrf) headers['X-CSRF-Token'] = csrf.getAttribute('content') || '';
    return headers;
  }

  /** Handles unauthorized responses by clearing tokens and emitting expiration events. */
  private handleUnauthorized(): void {
    try {
      this.tokenStorage.clearToken();
    } catch {
      /* Storage may fail (quota, private mode); still emit session event. */
    }
    try {
      globalThis.dispatchEvent(new CustomEvent(AUTH_SESSION_EXPIRED_EVENT));
    } catch {
      /* A faulty listener must not swallow the HTTP error path. */
    }
  }

  /** Logs an HTTP error and throws an exception with the provided message. */
  private throwLoggedHttpError(requestUrl: string, correlationId: string, status: number, message: string): never {
    this.telemetry.logError(`HTTP ${status} ${requestUrl}`, { correlationId, status, requestUrl, message });
    throw new Error(message);
  }

  /** Logs a 403 Forbidden error and throws an exception. */
  private throwForbidden(requestUrl: string, correlationId: string): never {
    this.telemetry.logError(`HTTP 403 ${requestUrl}`, { correlationId, status: 403, requestUrl });
    throw new Error('Forbidden: You do not have permissions to perform this action.');
  }

  /** Processes the HTTP response, handling errors and optionally parsing JSON. */
  private async handleResponse<T>(
    response: HttpTransportResponse,
    parseAsJson: boolean,
    requestOptions: HttpClientOptions | undefined,
    correlationId: string,
    requestUrl: string,
  ): Promise<T> {
    if (response.status === 401) {
      if (!requestOptions?.treat401AsPlainError) this.handleUnauthorized();
      this.throwLoggedHttpError(requestUrl, correlationId, 401, await this.parseError(response));
    }
    if (response.status === 403) this.throwForbidden(requestUrl, correlationId);
    if (!response.ok) {
      this.throwLoggedHttpError(requestUrl, correlationId, response.status, await this.parseError(response));
    }
    if (!parseAsJson || response.status === 204) return true as T;
    const text = await response.text();
    if (!text.trim()) return true as T;
    try {
      return JSON.parse(text) as T;
    } catch {
      this.telemetry.logError('Invalid JSON response from server', { correlationId, requestUrl });
      throw new Error('Invalid JSON response from server');
    }
  }

  /** Executes an HTTP request with retries and response handling. */
  private async exchange<T>(endpoint: string, parseJson: boolean, retriesLeft: number, init: HttpClientOptions): Promise<T> {
    const url = `${AppConfig.getApiBaseUrl()}${endpoint}`;
    const { response, correlationId } = await fetchWithTimeoutAndRetry(url, init, retriesLeft, {
      telemetry: this.telemetry,
      defaultTimeout: this.defaultTimeout,
      retryBackoff: this.retryBackoff,
      buildHeaders: (extra, cid) => this.getAuthHeaders(extra, cid),
    });
    return this.handleResponse<T>(response, parseJson, init, correlationId, url);
  }

  /** Performs an HTTP GET request to the specified endpoint. */
  async get<T>(endpoint: string, options?: HttpClientOptions): Promise<T> {
    return this.exchange<T>(endpoint, true, options?.retries ?? this.maxRetries, {
      ...options,
      method: 'GET',
      headers: options?.headers,
    });
  }

  /** Performs an HTTP POST or PUT request with a JSON body. */
  private async jsonWrite<T>(method: 'POST' | 'PUT', endpoint: string, data?: unknown, options?: HttpClientOptions): Promise<T> {
    let body: string | undefined;
    if (data !== undefined) {
      try {
        body = JSON.stringify(data);
      } catch {
        throw new Error('El cuerpo de la petición no es JSON serializable (referencias circulares u objetos no válidos).');
      }
    }
    return this.exchange<T>(endpoint, true, options?.retries ?? 0, {
      ...options,
      method,
      headers: { 'Content-Type': 'application/json', ...options?.headers },
      body,
    });
  }

  /** Performs an HTTP POST request with a JSON body. */
  async post<T>(endpoint: string, data?: unknown, options?: HttpClientOptions): Promise<T> {
    return this.jsonWrite<T>('POST', endpoint, data, options);
  }

  /** Performs an HTTP PUT request with a JSON body. */
  async put<T>(endpoint: string, data?: unknown, options?: HttpClientOptions): Promise<T> {
    return this.jsonWrite<T>('PUT', endpoint, data, options);
  }

  /** Performs an HTTP DELETE request to the specified endpoint. */
  async delete(endpoint: string, options?: HttpClientOptions): Promise<boolean> {
    return this.exchange<boolean>(endpoint, false, options?.retries ?? 0, {
      ...options,
      method: 'DELETE',
      headers: options?.headers,
    });
  }

  /** Parses an error response body into a readable string message. */
  private async parseError(response: HttpTransportResponse): Promise<string> {
    const text = await response.text().catch(() => '');
    const trimmed = text.trim();
    if (!trimmed) return `Error: HTTP ${response.status}`;
    try {
      const err = JSON.parse(trimmed) as { error?: string; message?: string };
      return err.error ?? err.message ?? `Error: HTTP ${response.status}`;
    } catch {
      return trimmed.length > 280 ? `${trimmed.slice(0, 280)}…` : trimmed;
    }
  }
}

/** Creates a new HttpClient instance with the given dependencies. */
export function createHttpClient(
  tokenStorage: HttpTokenStorage,
  telemetry: TelemetryPort,
): HttpClient {
  return new HttpClient(tokenStorage, telemetry);
}
